import React, {
  useContext, useCallback, useState, useMemo, useRef
} from 'react';
import {
  bool, shape, string, number
} from 'prop-types';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MajorApplianceFulfillment } from '@thd-olt-component-react/fulfillment';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { AddTo } from '@thd-olt-component-react/add-to';
import { PaypalPriceMessaging } from '@thd-olt-component-react/paypal-checkout-button';
import classNames from 'classnames';
import {
  alias,
  params,
  client,
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  shape as shapeType,
  string as stringType,
  number as numberType,
  bool as boolType,
  arrayOf as arrayOfType
} from '@thd-nucleus/data-sources/react/dataModel/prop-types';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { BuyboxAddToProject } from './subcomponents/BuyboxAddToProject';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import * as helper from '../BuyboxHelper';
import { FULFILLMENT_METHOD_APPLIANCE
  , ATC_TEXT, BOGO_ATC_TEXT } from '../constants';
import { isMobile } from '../util';
import { useBogoInfo } from './hooks/useBogoInfo';

import { BuyboxAddToList } from './subcomponents/BuyboxAddToList';
import useConfiguratorEvents from './hooks/useConfiguratorEvents';

/**
 * Buybox container
 */
const MajorApplianceBuyBox = (props) => {
  const {
    channel,
    deliveryZip,
    isServer,
    isConsumerApp
  } = useContext(ExperienceContext);
  const {
    stash,
    showCards,
    sticky,
    showFavorites,
    showAddToProject
  } = props;

  const buyboxActionsRef = useRef(null);

  const bogoInfo = useBogoInfo();

  let { itemId } = props;

  const covidAlert = useConfigService('fs-prop:buy-area__buybox--covidAlert');
  const bopisMinimumThreshold = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThreshold');
  const bopisMinimumThresholdStores = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThresholdStores');
  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const enableAddToProjectFS = useConfigService('fs:enableAddToProjectBuyBox');
  const enableAssortmentStrategyDiscontinued = useConfigService('enableAssortmentStrategyDiscontinued') || false;
  const isAddToProjectsEnabled = enableAddToProjectFS && showAddToProject;

  const theme = useTheme(MajorApplianceBuyBox);
  const { hidePaypal } = theme.props;

  const isTwoTileEnabledFS = isTwoTileEnabledGlobal;
  const { storeId, storeZip } = useStore();
  const { configuratorUnavailable } = useConfiguratorEvents();
  const state = {
    fulfillmentMethod: FULFILLMENT_METHOD_APPLIANCE,
    isShowATC: false,
    zipCode: '',
    disableATC: false,
    quantity: 1
  };
  const getItemIdFromConfigurator = (items) => {
    if (items) {
      return items.find((item) => item.isAnchor).itemId.toString();
    }
    return '';
  };
  const [buyboxState, setBuyboxState] = useState(state);
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  let configurableApplianceItemIds;
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
    configurableApplianceItemIds = items.map((item) => item.itemId.toString());
  }
  const { configuratorData } = useLifeCycleEventBus('configurator.configurator_data');
  const configuratorCart = configuratorData?.product?.configuration?.cart;
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  const productOptions = {
    ssr: false,
    variables: {
      itemId,
      storeId,
      zipCode: storeZip
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  const {
    fulfillment = {},
    pricing = {}
  } = data?.product || {};

  const { data: availabilityData } = useDataModel('checkAvailability', {
    variables: {
      itemIds: [itemId],
      zipCode: deliveryZip,
      localStoreId: storeId
    },
    ssr: false,
    skip: !enableAssortmentStrategyDiscontinued || !itemId || !deliveryZip || !storeId
  });

  let isDiscontinued = false;

  if (enableAssortmentStrategyDiscontinued) {
    isDiscontinued = availabilityData?.checkAvailability?.fulfillmentOptions?.some(
      (option) => option?.services?.some(
        (service) => service?.deliveryAvailabilityDetail?.some(
          (detail) => detail?.assortmentStrategy === 'Inactive Discontinue'
        )
      )
    );
  }

  const isPaypalEnabled = useConfigService('PAYPAL_ENABLE');
  const isPaypalPayInFour = useConfigService('paypalPayInFour');
  const isPromoPIP = useMemo(() => {
    if (pricing?.conditionalPromotions) {
      return pricing?.conditionalPromotions.find((promotion) => {
        return (promotion.experienceTag === 'BMSM'
          || promotion.experienceTag === 'MSBONLINE'
          || promotion.experienceTag === 'BOGO');
      });
    }
    return false;
  }, [pricing?.conditionalPromotions]);
  const isMAPPrice = pricing?.message === 'see final price in cart';

  const showPaypalMessaging = !hidePaypal && isPaypalEnabled && isPaypalPayInFour;
  const isGenericMessaging = isMAPPrice || isPromoPIP;

  const onFulfillmentChange = ({
    method,
    isShowATC,
    zipCode,
    disableATC,
    primaryStoreNum
  }) => {
    setBuyboxState((prevState) => {

      return {
        ...prevState,
        fulfillmentMethod: method,
        isShowATC,
        zipCode,
        disableATC,
        primaryStoreNum
      };
    });
  };

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', atcStash);
    }
  }, []);

  const {
    isShowATC,
    quantity,
    zipCode,
    fulfillmentMethod,
    disableATC,
    primaryStoreNum
  } = buyboxState;

  if ((error && !data)) {
    return (
      <BuyboxError />
    );
  }

  if (loading || isServer) {
    return (
      <BuyboxLoader tilesCount={2} />
    );
  }

  const buttonText = bogoInfo?.bogoSelectedProduct?.itemId ? BOGO_ATC_TEXT : ATC_TEXT;

  const cartReqParams = configuratorCart
  || helper.getCartReqParams({
    itemId,
    bogoInfo,
    fulfillmentMethod,
    configurableItems: items,
    quantity,
    zipCode,
    storeId,
    fulfillment,
    primaryStoreNum
  });

  const cartOptions = helper.getCartOptions({
    channel,
    configurableItems: items,
    bogoInfo,
    fulfillmentMethod,
    alterBrowserHistory: true,
    zipCode,
    storeId
  });
  if (!data) {
    return null;
  }

  const stickyClassnames = classNames('sui-bg-primary sui-left-0 sui-flex sui-flex-col sui-w-full sui-p-4  sui-gap-4', {
    'buybox__actions--sticky sui-d-none': !isConsumerApp,
  });

  const addToCartNode = (
    <AddToCart
      cartOptions={cartOptions}
      cartReqParams={cartReqParams}
      onClick={onATCClick}
      disabled={configuratorUnavailable || disableATC}
      showIcon
    >
      {buttonText}
    </AddToCart>
  );

  const addToListNode = (
    <div className={!isAddToProjectsEnabled ? 'sui-col-span-2' : ''}>
      <BuyboxAddToList quantity={quantity} itemId={itemId} />
    </div>
  );

  const addToProjectsNode = (
    <div className={!showFavorites ? 'sui-col-span-2' : ''}>
      <BuyboxAddToProject
        itemId={itemId}
        quantity={quantity}
      />
    </div>
  );

  const showATC = isShowATC && !isDiscontinued;

  return (
    <div
      className={'sui-grow sui-overflow-hidden sui-w-full ' + (isMobile(channel) && 'sui-mb-4')}
      data-section="MajorApplianceBuyBox"
      data-component="MajorApplianceBuyBox"
    >
      {sticky && isMobile(channel) && (
        <StickyWithHeaderObserver
          boxShadow
          className={stickyClassnames}
          childRef={buyboxActionsRef}
          forwardRef={buyboxActionsRef}
          offsetScroll={18}
          position="bottom"
          stickyByDefault
          stickyType="forwardRef"
          visibleByDefault
        >
          {showATC && (
            <div className="sui-flex sui-flex-col sui-gap-4">
              <div className="sui-flex sui-flex-col">
                {addToCartNode}
              </div>
              {(showFavorites || isAddToProjectsEnabled) && (
                <div className="sui-grid sui-grid-cols-2 sui-gap-4">
                  {showFavorites && addToListNode}
                  {isAddToProjectsEnabled && addToProjectsNode}
                </div>
              )}
            </div>
          )}
        </StickyWithHeaderObserver>
      )}
      <div className="sui-bg-subtle">
        <div className="sui-bg-primary sui-w-full sui-p-4 sui-mb-4">
          <MajorApplianceFulfillment
            itemId={itemId}
            configurableApplianceItemIds={configurableApplianceItemIds}
            covidAlert={covidAlert}
            quantity={quantity}
            onChange={onFulfillmentChange}
            bopisMinimumThreshold={bopisMinimumThreshold}
            bopisMinimumThresholdStores={bopisMinimumThresholdStores}
            isTwoTileEnabledFS={isTwoTileEnabledFS}
          />
        </div>
        {showATC && (
          <div
            className="sui-bg-primary sui-w-full sui-p-4"
            ref={buyboxActionsRef}
          >
            <div className="sui-flex sui-flex-col sui-w-full sm:sui-w-96 sui-gap-4">
              <div className="sui-flex sui-flex-col">
                {addToCartNode}
              </div>
              {(showFavorites || isAddToProjectsEnabled) && (
                <div className="sui-grid sui-grid-cols-2 sui-gap-4">
                  {showFavorites && addToListNode}
                  {isAddToProjectsEnabled && addToProjectsNode}
                </div>
              )}
              { showPaypalMessaging && (
                <div className="sui-w-full">
                  <PaypalPriceMessaging
                    payInFour
                    orderTotal={isGenericMessaging ? null : pricing?.value}
                    fromPage="pip"
                    fontSize="14"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MajorApplianceBuyBox.displayName = 'MajorApplianceBuyBox';

MajorApplianceBuyBox.themeProps = {
  hidePaypal: bool
};

MajorApplianceBuyBox.defaultThemeProps = {
  hidePaypal: false
};

MajorApplianceBuyBox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  showCards: bool,
  sticky: bool,
  showFavorites: bool,
  showAddToProject: bool
};

MajorApplianceBuyBox.defaultProps = {
  showFavorites: false,
  itemId: null,
  stash: {},
  sticky: false,
  showCards: false,
  showAddToProject: false
};

export const majorApplianceDataModel = {
  clientOnlyProduct: alias('product').params({
    itemId: stringType().isRequired()
  }).shape({
    identifiers: shapeType({
      productLabel: stringType()
    }),
    fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
      fulfillmentOptions: arrayOfType(shapeType({
        type: stringType(),
        fulfillable: boolType(),
        services: arrayOfType(shapeType({
          locations: arrayOfType(shapeType({
            inventory: shapeType({
              quantity: numberType()
            }),
            isAnchor: boolType(),
            locationId: stringType(),
            storeName: stringType(),
            type: stringType()
          })),
          type: stringType()
        }))
      }))
    }))
  })
};

MajorApplianceBuyBox.dataModel = extend(
  majorApplianceDataModel,
  MajorApplianceFulfillment,
  AddToList,
  AddTo
);

const ErrorMABuybox = withErrorBoundary(MajorApplianceBuyBox);

export { ErrorMABuybox as MajorApplianceBuyBox };
